import {
    computed,
    type Raw,
    reactive,
    type Reactive,
    ref,
    type Ref,
    watch,
    watchEffect,
} from 'vue';
import {
    Plane,
    Vector3,
    BufferGeometry,
    LineSegments,
    Matrix4,
    LineBasicMaterial
} from 'three';
import { stop, stopAll, type StopHandle, taggedLogger } from '@/util';
import type { SceneContext } from '@/lib/threeScene/SceneContext';
import { watchImmediate } from '@vueuse/core';
import type { NodeId } from '@/lib/threeScene/nodeId';
import { isMeshNode } from '@/lib/threeScene/nodes/mesh';
import { meshPlaneIntersectionPoints } from '@/domain/geometry/meshPlaneIntersection';

const log = taggedLogger('3D');

/**
 * Add and update a line representing the intersection of a mesh and a plane.
 *
 * @param context the scene-context
 * @param parentId id for the parent-node to attach the line to. If null, the scene is used.
 * @param planeId the id of the plane-node to intersect
 * @param meshId the id of the mesh-node to intersect
 * @param material material to apply to the line.
 */
export function updatePlaneMeshIntersection(
    context: SceneContext,
    parentId: NodeId | null,
    planeId: NodeId,
    meshId: NodeId,
    material: Ref<Raw<LineBasicMaterial>>,
): StopHandle {
    const parentObject = parentId !== null ? context.getObject(parentId) : context.scene;
    const parentTransform: Ref<Matrix4> =
        parentId !== null ? computed(() => context.worldTransform(parentId)) : ref(new Matrix4());

    if (!context.tryGetNode(planeId)) {
        throw new Error(`Could not find plane-node ${planeId} to intersect`);
    }

    const meshNode = context.getNode(meshId);
    if (!isMeshNode(meshNode)) {
        throw new Error(`Type of mesh-node ${meshId} to intersect is '${meshNode.type}', expected 'mesh'`);
    }

    const lineSegments = new LineSegments(new BufferGeometry(), material.value);
    lineSegments.matrixAutoUpdate = false;

    parentObject.add(lineSegments);

    const worldPlane = reactive(new Plane());

    let update: StopHandle | null = null;

    return stopAll(
        watch(material, (material) => {
            lineSegments.material = material;
        }),
        watchImmediate(
            () =>
                [
                    parentTransform.value,
                    context.worldTransform(meshId),
                    context.worldTransform(planeId),
                ] as const,
            ([parentTransform, meshTransform, planeTransform]) => {
                stop(update);
                worldPlane.copy(
                    new Plane().setFromNormalAndCoplanarPoint(
                        new Vector3().setFromMatrixColumn(planeTransform, 2),
                        new Vector3().setFromMatrixPosition(planeTransform),
                    ),
                );
                const worldToParent = parentTransform.clone().invert();
                lineSegments.matrix.copy(meshTransform).premultiply(worldToParent);
                lineSegments.updateMatrixWorld();

                update = updatePlaneMeshIntersectionSegments(
                    lineSegments,
                    worldPlane,
                    computed(() => meshNode.geometry),
                    ref(meshTransform),
                );
            },
            { deep: true },
        ),
        () => {
            stop(update);
            parentObject.remove(lineSegments);
            lineSegments.geometry.dispose();
        },
    );
}

/**
 * Update a cross-section-outline
 */
export function updatePlaneMeshIntersectionSegments(
    lineSegments: LineSegments,
    plane: Reactive<Plane>,
    meshGeometry: Ref<BufferGeometry | null>,
    meshTransform: Ref<Matrix4>,
): StopHandle {
    return watchEffect(() => {
        lineSegments.geometry.dispose();

        if (!meshGeometry.value) {
            lineSegments.geometry = new BufferGeometry();
            return;
        }

        if (!meshGeometry.value.index) {
            log.error('Geometry has no index');
            lineSegments.geometry.dispose();
            lineSegments.geometry = new BufferGeometry();
            return;
        }

        // Convert the plane to local-mesh-space
        const worldToMesh = meshTransform.value.clone().invert();
        const localPlane = new Plane().copy(plane).applyMatrix4(worldToMesh);

        const segmentPoints = meshPlaneIntersectionPoints(meshGeometry.value, localPlane);
        lineSegments.geometry = new BufferGeometry().setFromPoints(segmentPoints);
    });
}